export interface ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
  // Parsed JSON body of the error response, when the server sent one.
  body?: unknown;
}

class ApiClient {
  private baseUrl: string;
  private timeout: number;

  constructor(baseUrl: string = resolveApiBaseUrl(), timeout: number = 10000) {
    this.baseUrl = baseUrl;
    this.timeout = timeout;
  }

  private getAccessToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("access_token");
  }

  async refreshAccessToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) return null;

    try {
      const response = await fetch(`${this.baseUrl}/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        return null;
      }

      const data = await response.json();
      localStorage.setItem("access_token", data.accessToken);
      return data.accessToken;
    } catch {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      return null;
    }
  }

  private async fetchWithAuth<T>(
    method: string,
    path: string,
    options: {
      body?: unknown;
      headers?: Record<string, string>;
      retryCount?: number;
    } = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    const accessToken = this.getAccessToken();
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      });

      if (response.status === 401 && accessToken && (options.retryCount ?? 0) < 1) {
        const newToken = await this.refreshAccessToken();
        if (newToken) {
          return this.fetchWithAuth(method, path, { ...options, retryCount: 1 });
        }
        if (typeof window !== "undefined") {
          window.location.href = "/auth/login";
        }
      }

      if (!response.ok) {
        throw await this.parseError(response);
      }

      if (response.status === 204) {
        return undefined as T;
      }

      return response.json();
    } catch (err: any) {
      if (err?.status) throw err;
      const error = new Error("Unable to reach the server. Please try again.") as ApiError;
      error.status = 0;
      error.code = "NETWORK_ERROR";
      error.details = err?.message || String(err);
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private async parseError(response: Response): Promise<ApiError> {
    const error = new Error(`API Error: ${response.statusText}`) as ApiError;
    error.status = response.status;
    error.code = `HTTP_${response.status}`;
    try {
      const body = await response.text();
      if (body) {
        error.details = body;
        try {
          const parsed = JSON.parse(body);
          if (parsed?.message) {
            error.message = parsed.message;
          }
          // Stash the parsed JSON body so callers can read structured
          // error payloads (e.g. 409 { tripId } for save-shared).
          (error as ApiError & { body?: unknown }).body = parsed;
        } catch {
          // Keep default message
        }
      }
    } catch {
      // Response body already read or empty
    }
    return error;
  }

  async get<T>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.fetchWithAuth("GET", path, { headers });
  }

  async post<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.fetchWithAuth("POST", path, { body, headers });
  }

  async put<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.fetchWithAuth("PUT", path, { body, headers });
  }

  async delete<T>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.fetchWithAuth("DELETE", path, { headers });
  }
}

export const apiClient = new ApiClient();

export function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL;
  if (configured) return configured;
  if (typeof window === "undefined") return "http://localhost:8080";
  const host = window.location.hostname;
  if (host === "travel.sigpush.com") {
    return "https://api.travel.sigpush.com";
  }
  if (host.endsWith(".sigpush.com")) {
    return `https://api.${host}`;
  }
  return "http://localhost:8080";
}
